import React from 'react';
import { ISupportHoursTable } from '../../SupportHoursTypes';
import {
  DataGrid,
  DynamicPagination,
  FlexGrid,
  getClassNameFactory,
  Responsive,
  SearchInput,
  Sizes,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
} from '@dcl/ui';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import './SupportHoursTable.css';
import {
  DESKTOP_MIN_WIDTH,
  ITEMS_PER_PAGE_VALUE,
  MOBILE_MAX_WIDTH,
  NEXT_LABEL_VALUE,
  PAGE,
  PAGINATION_CONFIG,
  PAGINATION_LABEL_VALUE,
  PREVIOUS_LABEL_VALUE,
  RESULTS_PER_PAGE_LABEL_VALUE,
  ROWS_PER_PAGE_LABEL_CONTENT,
} from '../../constants';
import {
  CustomColumn,
  IPaginationBarProps,
} from '@dcl/ui/dist/fragments/DataGrid/DataGridTypes';
import { tableBodyData, tableHeadings } from '../../mock';
import { SupportHoursMobileTable } from '../SupportHoursMobileTable/SupportHoursMobileTable';

export const SupportHoursTable: React.FC<ISupportHoursTable> = (props) => {
  const getClassName = getClassNameFactory(SupportHoursTable.displayName);
  const { searchTitle } = props;
  const inputSearchRef = React.useRef<SearchInput>(null);

  function handleClearSearch() {
    if (inputSearchRef.current) {
      inputSearchRef.current.value = '';
    }
  }

  function handleSearch(searchValue: string) {
    if (inputSearchRef.current) {
      inputSearchRef.current.value = searchValue;
    }
  }

  const renderPagination = (paginationProps: IPaginationBarProps) => {
    const {
      pageCount,
      pageSize: itemsPerPage,
      numberOfVisiblePages,
      currentPage,
      rowsPerPageValues,
      onPageChange,
      onPageSizeChange,
    } = paginationProps;

    return (
      <div className={getClassName({ descendantName: 'paginationDesktop' })}>
        <DynamicPagination
          numberOfVisiblePages={numberOfVisiblePages}
          nextButtonLabel={NEXT_LABEL_VALUE}
          previousButtonLabel={PREVIOUS_LABEL_VALUE}
          paginationLabel={PAGINATION_LABEL_VALUE}
          rowsPerPageLabel={RESULTS_PER_PAGE_LABEL_VALUE}
          rowsPerPageLabelContent={ROWS_PER_PAGE_LABEL_CONTENT}
          viewingLabel={PAGE}
          rowsPerPageValues={rowsPerPageValues}
          goToPageError={'Enter a valid number'}
          totalPages={pageCount}
          totalItems={tableBodyData.length}
          pageSize={itemsPerPage}
          startFromPage={currentPage}
          onPageChange={(pageChangeItem) => {
            onPageChange(pageChangeItem);
          }}
          onPageSizeChange={(pageSizeItem) => {
            onPageSizeChange(pageSizeItem);
          }}
        />
      </div>
    );
  };

  return (
    <div className={getClassName()}>
      <VerticalSpacing size={VerticalSpacingSizes.S}> </VerticalSpacing>
      <FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'flexRow' })}>
          <FlexGrid.Col
            className={getClassName({ descendantName: 'searchTitle' })}
          >
            <Typography type={Sizes.XXTRASMALL} tag={TypographyTags.SPAN}>
              {searchTitle}
            </Typography>
          </FlexGrid.Col>
          <FlexGrid.Col
            md={4}
            lg={4}
            sm={12}
            className={getClassName({ descendantName: 'searchBox' })}
          >
            <SearchInput
              ref={inputSearchRef}
              placeholder={''}
              typeahead={false}
              value={''}
              colorTheme="orange"
              onSubmit={(searchValue: string) => handleSearch(searchValue)}
              data-testid="searchInputBox"
              onClear={() => handleClearSearch()}
              allowEmptyInput={true}
            />
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row className={getClassName({ descendantName: 'tableRow' })}>
          <FlexGrid.Col
            md={12}
            lg={12}
            sm={12}
            className={getClassName({ descendantName: 'table' })}
          >
            <Responsive minWidth={DESKTOP_MIN_WIDTH}>
              <DataGrid
                columns={tableHeadings}
                data={tableBodyData}
                sorting={{
                  ariaSortDescendingName: 'descending',
                  ariaSortAscendingName: 'ascending',
                  disableMultiSort: true,
                  manualSortBy: false,
                  autoResetSortBy: false,
                  disableSortRemove: false,
                }}
                renderPagination={renderPagination}
                pagination={{
                  itemsPerPageValues: PAGINATION_CONFIG,
                  itemsPerPage: ITEMS_PER_PAGE_VALUE,
                }}
              />
            </Responsive>
            <Responsive minWidth={0} maxWidth={MOBILE_MAX_WIDTH}>
              <SupportHoursMobileTable
                data={tableBodyData}
                columns={tableHeadings as CustomColumn<object>[]}
              ></SupportHoursMobileTable>
            </Responsive>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};
SupportHoursTable.displayName = 'SupportHoursTable';
